// hooks/useUserUpdate.ts
import { useCallback, type Dispatch, type SetStateAction } from 'react';
import { userService } from '../services/userService';
import type { User } from '@supabase/supabase-js';
import type { DbUser } from '../contexts/AppContext';
import type { PlanOperations } from './useAcademicPlan';

// =============================================================================
// TYPES
// =============================================================================

// Re-exported so existing imports of `useUserUpdate.DbUser` keep working.
// The canonical source is AppContext.
export type { DbUser } from '../contexts/AppContext';

/**
 * Subset of plan operations needed by useUserUpdate. We only call refreshPlan,
 * but accept the full PlanOperations so the hook can be wired without a
 * narrowing wrapper at the call site.
 */
export type PlanOps = Pick<PlanOperations, 'refreshPlan'>;

/**
 * Parameters for the useUserUpdate hook
 */
export interface UseUserUpdateParams {
  user: User | null;
  setDbUser: Dispatch<SetStateAction<DbUser | null>>;
  setSyncingUser: Dispatch<SetStateAction<boolean>>;
  planOps: PlanOps | null;
  showError: (message: string, options?: Record<string, unknown>) => void;
}

/**
 * Callback function type for after update
 */
export type AfterUpdateCallback = () => Promise<void> | void;

/**
 * Return type for the useUserUpdate hook
 */
export interface UseUserUpdateReturn {
  handleUserUpdate: (resetPlan?: boolean, afterUpdateCallback?: AfterUpdateCallback) => Promise<void>;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook to handle user data updates and profile refresh.
 * Extracts user update logic from App.js Header onUserUpdate callback.
 */
export function useUserUpdate({
  user,
  setDbUser,
  setSyncingUser,
  planOps,
  showError
}: UseUserUpdateParams): UseUserUpdateReturn {
  const handleUserUpdate = useCallback(async (
    resetPlan = false,
    afterUpdateCallback?: AfterUpdateCallback
  ): Promise<void> => {
    if (!user) return;

    setSyncingUser(true);
    try {
      const response = await userService.syncUser(user);
      if (response) {
        setDbUser(response as unknown as DbUser);
        localStorage.setItem('academic_planner_db_user', JSON.stringify(response));
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      showError('Failed to refresh user data. Please try again.');
    } finally {
      setSyncingUser(false);
    }

    // Execute callback if provided
    if (afterUpdateCallback) {
      await afterUpdateCallback();
    }

    // For reset plans, ensure we wait a moment for backend to settle
    if (resetPlan) {
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    // Always refresh the academic plan when enrollment changes
    if (planOps && planOps.refreshPlan) {
      await planOps.refreshPlan();
    } else {
      console.error('refreshPlan not available, reloading page...');
      window.location.reload();
    }
  }, [user, setDbUser, setSyncingUser, planOps, showError]);

  return { handleUserUpdate };
}
